import { useNavigate } from "@tanstack/react-router"
import { memo, useState } from "react"
import { BsThreeDotsVertical } from "react-icons/bs"
import { FaEdit, FaUsers } from "react-icons/fa"
import { FiTrash2 } from "react-icons/fi"

import { Field } from "@/components/atoms/Field"
import { toaster } from "@/components/infrastructure"
import { GenerateSessionsMenuItem } from "@/features/classSessions"
import {
  useDeleteClassTemplate,
  useUpdateClassTemplate,
} from "@/hooks/api/useClassTemplates"
import { useSchoolSettings } from "@/hooks/api/useSchoolSettings"
import type {
  ClassTemplateResponse,
  ClassTemplateUpdateInput,
} from "@/types/class_template"
import { class_templateUpdateSchema } from "@/types/class_template"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Switch,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Controller,
  type Resolver,
  type SubmitHandler,
  useForm,
} from "react-hook-form"

interface ClassTemplateActionsMenuProps {
  classTemplate: ClassTemplateResponse
}

export const ClassTemplateActionsMenu = memo(
  ({ classTemplate }: ClassTemplateActionsMenuProps) => {
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const navigate = useNavigate()
    const { programTypes, swimLevels, locations } = useSchoolSettings()

    const deleteClassTemplateMutation = useDeleteClassTemplate()
    const updateClassTemplateMutation = useUpdateClassTemplate()

    const form = useForm<ClassTemplateUpdateInput>({
      resolver: zodResolver(
        class_templateUpdateSchema,
      ) as Resolver<ClassTemplateUpdateInput>,
      mode: "onBlur",
      defaultValues: {
        name: classTemplate.name ?? "",
        slug: classTemplate.slug ?? "",
        description: classTemplate.description ?? "",
        program_type: classTemplate.program_type ?? null,
        instructor_id: classTemplate.instructor_id ?? "",
        swim_level: classTemplate.swim_level ?? null,
        location: classTemplate.location ?? null,
        day_of_week: classTemplate.day_of_week ?? null,
        start_time: classTemplate.start_time ?? "",
        end_time: classTemplate.end_time ?? "",
        duration_minutes: classTemplate.duration_minutes ?? null,
        max_students: classTemplate.max_students ?? undefined,
        min_students: classTemplate.min_students ?? null,
        current_enrollment: classTemplate.current_enrollment ?? null,
        waitlist_enabled: classTemplate.waitlist_enabled ?? false,
        price: classTemplate.price ?? undefined,
        prerequisites: classTemplate.prerequisites ?? "",
        notes: classTemplate.notes ?? "",
        is_active: classTemplate.is_active ?? false,
        is_published: classTemplate.is_published ?? false,
      },
    })

    const getErrorText = (error: unknown): string | undefined => {
      if (typeof error === "object" && error !== null) {
        const maybeMessage = (error as { message?: unknown }).message
        if (typeof maybeMessage === "string") {
          return maybeMessage
        }
      }
      return undefined
    }

    const displayLabel = String(classTemplate.name ?? classTemplate.id)

    const handleDelete = async () => {
      try {
        await deleteClassTemplateMutation.mutateAsync(classTemplate.id)
        setShowDeleteDialog(false)
        toaster.create({
          title: "ClassTemplate deleted",
          description: `${displayLabel} has been deleted successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error deleting class_template:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to delete class_template"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage =
            "You don't have permission to delete this class_template"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Delete failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleUpdate: SubmitHandler<ClassTemplateUpdateInput> = async (
      data,
    ) => {
      try {
        await updateClassTemplateMutation.mutateAsync({
          class_templateId: classTemplate.id,
          class_templateData: data,
        })
        setShowEditDialog(false)
        form.reset({
          name: classTemplate.name ?? "",
          slug: classTemplate.slug ?? "",
          description: classTemplate.description ?? "",
          program_type: classTemplate.program_type ?? null,
          instructor_id: classTemplate.instructor_id ?? "",
          swim_level: classTemplate.swim_level ?? null,
          location: classTemplate.location ?? null,
          day_of_week: classTemplate.day_of_week ?? null,
          start_time: classTemplate.start_time ?? "",
          end_time: classTemplate.end_time ?? "",
          duration_minutes: classTemplate.duration_minutes ?? null,
          max_students: classTemplate.max_students ?? undefined,
          min_students: classTemplate.min_students ?? null,
          current_enrollment: classTemplate.current_enrollment ?? null,
          waitlist_enabled: classTemplate.waitlist_enabled ?? false,
          price: classTemplate.price ?? undefined,
          prerequisites: classTemplate.prerequisites ?? "",
          notes: classTemplate.notes ?? "",
          is_active: classTemplate.is_active ?? false,
          is_published: classTemplate.is_published ?? false,
        })
        toaster.create({
          title: "ClassTemplate updated",
          description: `${displayLabel} has been updated successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error updating class_template:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to update class_template"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to edit this class_template"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Update failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleEditClose = () => {
      setShowEditDialog(false)
      form.reset({
        name: classTemplate.name ?? "",
        slug: classTemplate.slug ?? "",
        description: classTemplate.description ?? "",
        program_type: classTemplate.program_type ?? null,
        instructor_id: classTemplate.instructor_id ?? "",
        swim_level: classTemplate.swim_level ?? null,
        location: classTemplate.location ?? null,
        day_of_week: classTemplate.day_of_week ?? null,
        start_time: classTemplate.start_time ?? "",
        end_time: classTemplate.end_time ?? "",
        duration_minutes: classTemplate.duration_minutes ?? null,
        max_students: classTemplate.max_students ?? undefined,
        min_students: classTemplate.min_students ?? null,
        current_enrollment: classTemplate.current_enrollment ?? null,
        waitlist_enabled: classTemplate.waitlist_enabled ?? false,
        price: classTemplate.price ?? undefined,
        prerequisites: classTemplate.prerequisites ?? "",
        notes: classTemplate.notes ?? "",
        is_active: classTemplate.is_active ?? false,
        is_published: classTemplate.is_published ?? false,
      })
    }

    return (
      <>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              variant="ghost"
              color="inherit"
              aria-label={`Actions for ${displayLabel}`}
            >
              <BsThreeDotsVertical />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <MenuItem value="edit" onClick={() => setShowEditDialog(true)}>
              <FaEdit fontSize="14px" aria-hidden="true" />
              Edit
            </MenuItem>
            <GenerateSessionsMenuItem
              classTemplateId={classTemplate.id}
              classTemplateName={classTemplate.name ?? ""}
            />
            <MenuItem
              value="view-enrollments"
              onClick={() =>
                navigate({
                  to: "/enrollments",
                  search: { class_template_id: classTemplate.id } as any,
                })
              }
            >
              <FaUsers fontSize="14px" aria-hidden="true" />
              View Enrollments
            </MenuItem>
            <MenuItem value="delete" onClick={() => setShowDeleteDialog(true)}>
              <FiTrash2 fontSize="14px" aria-hidden="true" />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>

        {/* Edit Dialog */}
        <DialogRoot
          open={showEditDialog}
          onOpenChange={(e) => !e.open && handleEditClose()}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent as="form" onSubmit={form.handleSubmit(handleUpdate)}>
            <DialogHeader>
              <DialogTitle>Edit ClassTemplate</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <VStack gap={6}>
                <Field
                  invalid={!!form.formState.errors.name}
                  errorText={getErrorText(form.formState.errors.name)}
                  label="Name"
                >
                  <Input
                    placeholder={"Enter name"}
                    aria-invalid={!!form.formState.errors.name}
                    {...form.register("name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.slug}
                  errorText={getErrorText(form.formState.errors.slug)}
                  label="Slug"
                >
                  <Input
                    placeholder={"Enter slug"}
                    aria-invalid={!!form.formState.errors.slug}
                    {...form.register("slug")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.description}
                  errorText={getErrorText(form.formState.errors.description)}
                  label="Description"
                >
                  <Textarea
                    {...form.register("description")}
                    placeholder={"Enter description"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.description}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.program_type}
                  errorText={getErrorText(form.formState.errors.program_type)}
                  label="Program Type"
                >
                  <select
                    {...form.register("program_type")}
                    aria-invalid={!!form.formState.errors.program_type}
                    className="chakra-select"
                  >
                    {programTypes.map((pt) => (
                      <option key={pt.value} value={pt.value}>{pt.label}</option>
                    ))}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.instructor_id}
                  errorText={getErrorText(form.formState.errors.instructor_id)}
                  label="Instructor Id"
                >
                  <Input
                    placeholder={"Enter instructor id"}
                    aria-invalid={!!form.formState.errors.instructor_id}
                    {...form.register("instructor_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.swim_level}
                  errorText={getErrorText(form.formState.errors.swim_level)}
                  label="Swim Level"
                >
                  <select
                    {...form.register("swim_level")}
                    aria-invalid={!!form.formState.errors.swim_level}
                    className="chakra-select"
                  >
                    {swimLevels.map((sl) => (
                      <option key={sl.value} value={sl.value}>{sl.label}</option>
                    ))}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.location}
                  errorText={getErrorText(form.formState.errors.location)}
                  label="Location"
                >
                  <select
                    {...form.register("location")}
                    aria-invalid={!!form.formState.errors.location}
                    className="chakra-select"
                  >
                    {locations.map((loc) => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.day_of_week}
                  errorText={getErrorText(form.formState.errors.day_of_week)}
                  label="Day Of Week"
                >
                  <select
                    {...form.register("day_of_week")}
                    aria-invalid={!!form.formState.errors.day_of_week}
                    className="chakra-select"
                  >
                    {" "}
                    <option value="Monday">Monday</option>{" "}
                    <option value="Tuesday">Tuesday</option>{" "}
                    <option value="Wednesday">Wednesday</option>{" "}
                    <option value="Thursday">Thursday</option>{" "}
                    <option value="Friday">Friday</option>{" "}
                    <option value="Saturday">Saturday</option>{" "}
                    <option value="Sunday">Sunday</option>{" "}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.start_time}
                  errorText={getErrorText(form.formState.errors.start_time)}
                  label="Start Time"
                >
                  <Input
                    placeholder={"Enter start time"}
                    aria-invalid={!!form.formState.errors.start_time}
                    {...form.register("start_time")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.end_time}
                  errorText={getErrorText(form.formState.errors.end_time)}
                  label="End Time"
                >
                  <Input
                    placeholder={"Enter end time"}
                    aria-invalid={!!form.formState.errors.end_time}
                    {...form.register("end_time")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.duration_minutes}
                  errorText={getErrorText(
                    form.formState.errors.duration_minutes,
                  )}
                  label="Duration Minutes"
                >
                  <Input
                    type="number"
                    step={1}
                    placeholder={"Enter duration minutes"}
                    aria-invalid={!!form.formState.errors.duration_minutes}
                    {...form.register("duration_minutes", {
                      valueAsNumber: true,
                    })}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.max_students}
                  errorText={getErrorText(form.formState.errors.max_students)}
                  label="Max Students"
                >
                  <Input
                    type="number"
                    step={1}
                    placeholder={"Enter max students"}
                    aria-invalid={!!form.formState.errors.max_students}
                    {...form.register("max_students", { valueAsNumber: true })}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.min_students}
                  errorText={getErrorText(form.formState.errors.min_students)}
                  label="Min Students"
                >
                  <Input
                    type="number"
                    step={1}
                    placeholder={"Enter min students"}
                    aria-invalid={!!form.formState.errors.min_students}
                    {...form.register("min_students", { valueAsNumber: true })}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.current_enrollment}
                  errorText={getErrorText(
                    form.formState.errors.current_enrollment,
                  )}
                  label="Current Enrollment"
                >
                  <Input
                    type="number"
                    step={1}
                    placeholder={"Enter current enrollment"}
                    aria-invalid={!!form.formState.errors.current_enrollment}
                    {...form.register("current_enrollment", {
                      valueAsNumber: true,
                    })}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.waitlist_enabled}
                  errorText={getErrorText(
                    form.formState.errors.waitlist_enabled,
                  )}
                  label="Waitlist Enabled"
                >
                  <Controller
                    name="waitlist_enabled"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="waitlist_enabled"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Waitlist Enabled"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.price}
                  errorText={getErrorText(form.formState.errors.price)}
                  label="Price Per Session"
                >
                  <Input
                    type="number"
                    step={0.01}
                    placeholder={"Enter price per session"}
                    aria-invalid={!!form.formState.errors.price}
                    {...form.register("price", { valueAsNumber: true })}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.prerequisites}
                  errorText={getErrorText(form.formState.errors.prerequisites)}
                  label="Prerequisites"
                >
                  <Textarea
                    {...form.register("prerequisites")}
                    placeholder={"Enter prerequisites"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.prerequisites}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.notes}
                  errorText={getErrorText(form.formState.errors.notes)}
                  label="Notes"
                >
                  <Textarea
                    {...form.register("notes")}
                    placeholder={"Enter notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.notes}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.is_active}
                  errorText={getErrorText(form.formState.errors.is_active)}
                  label="Is Active"
                >
                  <Controller
                    name="is_active"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="is_active"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Is Active"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.is_published}
                  errorText={getErrorText(form.formState.errors.is_published)}
                  label="Is Published"
                >
                  <Controller
                    name="is_published"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="is_published"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Is Published"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={handleEditClose}
                  disabled={updateClassTemplateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  loading={updateClassTemplateMutation.isPending}
                  loadingText="Updating..."
                >
                  Update ClassTemplate
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        {/* Delete Dialog */}
        <DialogRoot
          open={showDeleteDialog}
          onOpenChange={(e) => !e.open && setShowDeleteDialog(false)}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete ClassTemplate</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <Box
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={4}
                mb={4}
              >
                <Text color="yellow.800">
                  Are you sure you want to delete{" "}
                  <strong>"{displayLabel}"</strong>?
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.600">
                This action cannot be undone. The classTemplate will be
                permanently removed from the system.
              </Text>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteClassTemplateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={deleteClassTemplateMutation.isPending}
                >
                  {deleteClassTemplateMutation.isPending
                    ? "Deleting..."
                    : "Delete ClassTemplate"}
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </>
    )
  },
)

ClassTemplateActionsMenu.displayName = "ClassTemplateActionsMenu"

export default ClassTemplateActionsMenu
